import type { ComponentProps } from 'react'

import type { ResourceHeader } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

type StatusVariant = NonNullable<ComponentProps<typeof ResourceHeader>['status']>['variant']

export enum ReimbursableTypeEnum {
  TimeSettingsCustomTimeRangeCategory = 'TimeSettings::CustomTimeRangeCategory',
  FinanceCustomCategory = 'Finance::CustomCategory',
}

export enum PayrollRunStatusEnum {
  Opened = 'opened',
  InReview = 'in_review',
  Closed = 'closed',
  // Future status values (backend migration pending)
  Open = 'open',
  InPreparation = 'in_preparation',
  Calculation = 'calculation',
  Completed = 'completed',
}

type StatusConfig = {
  variant: StatusVariant
}

const createStatusConfig = <T extends Record<string, StatusConfig>>(config: T) => config

const PAYROLL_RUN_CYCLE_STATUS_CONFIG = createStatusConfig({
  [PayrollRunStatusEnum.Open]: {
    variant: 'neutral' as const,
  },
  [PayrollRunStatusEnum.InPreparation]: {
    variant: 'info' as const,
  },
  [PayrollRunStatusEnum.Calculation]: {
    variant: 'info' as const,
  },
  [PayrollRunStatusEnum.InReview]: {
    variant: 'warning' as const,
  },
  [PayrollRunStatusEnum.Completed]: {
    variant: 'positive' as const,
  },
})

export type PayrollRunCycleStatus = keyof typeof PAYROLL_RUN_CYCLE_STATUS_CONFIG

const LEGACY_STATUS_MAPPING = {
  [PayrollRunStatusEnum.Opened]: PayrollRunStatusEnum.Open,
  [PayrollRunStatusEnum.Closed]: PayrollRunStatusEnum.Completed,
} as const

type LegacyStatus = keyof typeof LEGACY_STATUS_MAPPING

const isPayrollRunCycleStatus = (status: string): status is PayrollRunCycleStatus => {
  return status in PAYROLL_RUN_CYCLE_STATUS_CONFIG
}

const isLegacyPayrollRunStatus = (status: string): status is LegacyStatus => {
  return status in LEGACY_STATUS_MAPPING
}

const getRawStatusConfig = (status: string | undefined) => {
  if (!status) {
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG.open
  }

  if (isPayrollRunCycleStatus(status)) {
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG[status]
  }

  if (isLegacyPayrollRunStatus(status)) {
    const mappedStatus = LEGACY_STATUS_MAPPING[status]
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG[mappedStatus]
  }

  return PAYROLL_RUN_CYCLE_STATUS_CONFIG.open
}

export const getPayrollRunStatusText = (status: string | undefined): string => {
  const normalizedStatus = (() => {
    if (!status) return PayrollRunStatusEnum.Open
    if (isPayrollRunCycleStatus(status)) return status
    if (isLegacyPayrollRunStatus(status)) return LEGACY_STATUS_MAPPING[status]
    return PayrollRunStatusEnum.Open
  })()

  switch (normalizedStatus) {
    case PayrollRunStatusEnum.Open:
      return i18n.t('compensations.PayrollRunCompensationsList.status.open')
    case PayrollRunStatusEnum.InPreparation:
      return i18n.t('compensations.PayrollRunCompensationsList.status.in_preparation')
    case PayrollRunStatusEnum.Calculation:
      return i18n.t('compensations.PayrollRunCompensationsList.status.calculation')
    case PayrollRunStatusEnum.InReview:
      return i18n.t('compensations.PayrollRunCompensationsList.status.in_review')
    case PayrollRunStatusEnum.Completed:
      return i18n.t('compensations.PayrollRunCompensationsList.status.completed')
    default:
      return i18n.t('compensations.PayrollRunCompensationsList.status.open')
  }
}

export const getPayrollRunStatusConfig = (status: string | undefined) => {
  return getRawStatusConfig(status)
}

export * from './currency'

export const COMPENSATIONS_IMPORT_THRESHOLD_MAPPING = 0.6
